import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link, useNavigate } from 'react-router-dom';
type Phase = 'baseline-collection' | 'work-baseline-output' | 'work-calibration' | 'integrated-work-output';
export function QuickWorkDecode() {
  const navigate = useNavigate();
  const [isAuthenticated] = useState(true);
  const [showLoginModal, setShowLoginModal] = useState(!isAuthenticated);
  const [hasBaseline] = useState(false);
  const [currentPhase, setCurrentPhase] = useState<Phase>(hasBaseline ? 'work-baseline-output' : 'baseline-collection');
  const [showExplanation, setShowExplanation] = useState(false);
  const [showWhatChanged, setShowWhatChanged] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [generatingMessage, setGeneratingMessage] = useState('');
  // Baseline data (reusing QuickSelfDecode structure)
  const [baselineData, setBaselineData] = useState({
    dateOfBirth: '1990-01-15',
    timeOfBirth: '',
    timeKnown: 'not-sure',
    placeOfBirth: '',
    country: '',
    year: '1990',
    month: '01',
    day: '15',
    hour: '12',
    minute: '00',
    period: 'AM'
  });
  // Work calibration data
  const [workCalibrationData, setWorkCalibrationData] = useState({
    pressureLevel: '',
    frictionZones: [] as string[],
    pressureMode: '',
    workContext: ''
  });
  // Work baseline output (structural translation)
  const workBaselineOutput = {
    workOrientation: 'You naturally operate with a preference for autonomy over constant guidance, structure over open-ended ambiguity, and depth over speed. You tend to work behind-the-scenes rather than in high-visibility roles, building systems and clarity before executing. This is not learned behavior — it is how this structure engages with work systems.',
    authorityResponse: 'Under authority and pressure, this system stabilizes by creating internal structure. It does not resist authority directly but may withdraw into analysis when directives feel unclear or misaligned. Over-functioning can occur when the system perceives chaos that needs organizing. The response is structural, not emotional.',
    workBlindSpot: 'This pattern can create friction when speed is prioritized over depth, or when visibility and quick output are rewarded more than systematic thinking. The system may delay action while seeking clarity that work environments do not always provide.'
  };
  // Integrated work output
  const integratedWorkOutput = {
    structuralBaseline: 'Structure-driven stabilizer. Prefers autonomy, depth, and behind-the-scenes system-building. Responds to pressure by creating internal clarity.',
    currentExpression: 'Right now, this pattern is running at high intensity, primarily activated by authority friction and ambiguity of role. You are aware of it in the moment, which creates space for recalibration rather than automatic response. The system is stabilizing under pressure but may be over-functioning to compensate for unclear directives.',
    workTensionSnapshot: 'A structure-driven stabilizer operating under high authority friction and role ambiguity.',
    whatChanged: {
      baseline: 'The baseline showed your structural work orientation (autonomy, depth, behind-the-scenes), authority response (stabilizing through internal structure), and blind spot (friction when speed trumps depth).',
      workContext: 'The work calibration revealed: current pressure level (high), specific friction zones (authority and role ambiguity), and pressure response mode (taking more control). This shows how the baseline pattern is expressing in your current work environment.',
      whyBaselineAlone: 'Baseline shows how you structurally operate at work. Work context shows where that pattern is activating right now, at what intensity, and under what specific conditions. Baseline is structure; work context is current expression.'
    }
  };
  const currentYear = new Date().getFullYear();
  const years = Array.from({
    length: currentYear - 1919
  }, (_, i) => currentYear - i);
  const months = [{
    value: '01',
    label: 'January'
  }, {
    value: '02',
    label: 'February'
  }, {
    value: '03',
    label: 'March'
  }, {
    value: '04',
    label: 'April'
  }, {
    value: '05',
    label: 'May'
  }, {
    value: '06',
    label: 'June'
  }, {
    value: '07',
    label: 'July'
  }, {
    value: '08',
    label: 'August'
  }, {
    value: '09',
    label: 'September'
  }, {
    value: '10',
    label: 'October'
  }, {
    value: '11',
    label: 'November'
  }, {
    value: '12',
    label: 'December'
  }];
  const getDaysInMonth = (year: string, month: string) => {
    if (!year || !month) return 31;
    return new Date(parseInt(year), parseInt(month), 0).getDate();
  };
  const updateDateOfBirth = (year: string, month: string, day: string) => {
    if (year && month && day) {
      const dateString = `${year}-${month}-${day}`;
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: dateString
      }));
    } else {
      setBaselineData(prev => ({
        ...prev,
        year,
        month,
        day,
        dateOfBirth: ''
      }));
    }
  };
  const convertTo24Hour = (hour: string, period: string): string => {
    let h = parseInt(hour);
    if (period === 'PM' && h !== 12) h += 12;
    if (period === 'AM' && h === 12) h = 0;
    return h.toString().padStart(2, '0');
  };
  const updateTimeOfBirth = (hour: string, minute: string, period: string) => {
    const hour24 = convertTo24Hour(hour, period);
    const timeString = `${hour24}:${minute}`;
    setBaselineData(prev => ({
      ...prev,
      hour,
      minute,
      period,
      timeOfBirth: timeString
    }));
  };
  const handleBaselineSubmit = () => {
    if (!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country) return;
    if ((baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth) return;
    setIsGenerating(true);
    setGeneratingMessage('Generating your structural baseline...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('work-baseline-output');
    }, 2000);
  };
  const handleWorkCalibrationSubmit = () => {
    if (!workCalibrationData.pressureLevel || workCalibrationData.frictionZones.length === 0 || !workCalibrationData.pressureMode) return;
    setIsGenerating(true);
    setGeneratingMessage('Refining your work snapshot...');
    setTimeout(() => {
      setIsGenerating(false);
      setCurrentPhase('integrated-work-output');
    }, 2000);
  };
  const handleToggleFriction = (friction: string) => {
    setWorkCalibrationData(prev => ({
      ...prev,
      frictionZones: prev.frictionZones.includes(friction) ? prev.frictionZones.filter(f => f !== friction) : [...prev.frictionZones, friction]
    }));
  };
  if (showLoginModal) {
    return <>
        <Helmet data-id="element-2673">
          <title data-id="element-2674">Quick Work Decode — Login Required | Lazy Monk Labs</title>
        </Helmet>
        <div className="fixed inset-0 bg-[#0A0A0A]/95 backdrop-blur-sm z-50 flex items-center justify-center px-6" data-id="element-2675">
          <div className="max-w-md w-full bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2676">
            <h2 className="text-2xl font-medium mb-4" data-id="element-2677">Save your snapshot</h2>
            <p className="text-[#F5F5F5]/70 mb-8 leading-relaxed" data-id="element-2678">
              Save your snapshot and return to it anytime.
            </p>
            <button onClick={() => setShowLoginModal(false)} className="w-full px-8 py-4 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors" data-id="element-2679">
              Continue with Google
            </button>
            <button onClick={() => navigate('/decode')} className="w-full mt-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-2680">
              Cancel
            </button>
          </div>
        </div>
      </>;
  }
  if (isGenerating) {
    return <>
        <Helmet data-id="element-2681">
          <title data-id="element-2682">Generating Work Snapshot | Lazy Monk Labs</title>
        </Helmet>
        <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5] flex items-center justify-center px-6" data-id="element-2683">
          <div className="text-center" data-id="element-2684">
            <div className="mb-6" data-id="element-2685">
              <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-[#F5F5F5]" data-id="element-2686"></div>
            </div>
            <p className="text-lg text-[#F5F5F5]/70" data-id="element-2687">{generatingMessage}</p>
          </div>
        </div>
      </>;
  }
  return <>
      <Helmet data-id="element-2688">
        <title data-id="element-2689">Quick Work Decode | Lazy Monk Labs</title>
      </Helmet>
      <div className="min-h-screen bg-[#0A0A0A] text-[#F5F5F5]" data-id="element-2690">
        <header data-id="element-2691">
          <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/95 backdrop-blur-sm border-b border-[#2B2B2B]" data-id="element-2692">
            <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between" data-id="element-2693">
              <Link to="/" className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-id="element-2694">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/jXd6KyuddrgAfqwwkkpUrR/Lazy_Monk_Labs_Logo.png" alt="Lazy Monk Labs Logo" className="h-8 w-auto" width="32" height="32" data-id="element-2695" />
                <span className="text-lg font-medium tracking-tight" data-id="element-2696">
                  Lazy Monk Labs
                </span>
              </Link>
              <Link to="/decode" className="text-sm text-[#F5F5F5]/80 hover:text-[#F5F5F5] transition-colors" data-id="element-2697">
                Back to Decode
              </Link>
            </div>
          </nav>
        </header>

        <main className="pt-24 pb-16 px-6" data-id="element-2698">
          <div className="max-w-2xl mx-auto" data-id="element-2699">
            {/* PHASE 1: Baseline Collection (Same as QuickSelfDecode) */}
            {currentPhase === 'baseline-collection' && <div data-id="element-2700">
                <div className="mb-12" data-id="element-2701">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2702">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2703">
                    Structural baseline
                  </p>
                </div>

                <div className="space-y-8 mb-12" data-id="element-2704">
                  {/* Date of Birth */}
                  <div data-id="element-2705">
                    <label className="block text-sm font-medium mb-3" data-id="element-2706">
                      Date of birth
                    </label>
                    {baselineData.dateOfBirth && <div className="mb-4 p-4 bg-[#111214] border border-[#2B2B2B] text-center" data-id="element-2707">
                        <div className="text-2xl font-medium" data-id="element-2708">
                          {months.find(m => m.value === baselineData.month)?.label}{' '}
                          {baselineData.day}, {baselineData.year}
                        </div>
                      </div>}
                    <div className="grid grid-cols-3 gap-3" data-id="element-2709">
                      <div data-id="element-2710">
                        <label htmlFor="month" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2711">
                          Month
                        </label>
                        <select id="month" value={baselineData.month} onChange={e => updateDateOfBirth(baselineData.year, e.target.value, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2712">
                          <option value="" data-id="element-2713">Month</option>
                          {months.map(m => <option key={m.value} value={m.value} data-id="element-2714">
                              {m.label}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2715">
                        <label htmlFor="day" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2716">
                          Day
                        </label>
                        <select id="day" value={baselineData.day} onChange={e => updateDateOfBirth(baselineData.year, baselineData.month, e.target.value)} disabled={!baselineData.month || !baselineData.year} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors disabled:opacity-30" data-id="element-2717">
                          <option value="" data-id="element-2718">Day</option>
                          {Array.from({
                        length: getDaysInMonth(baselineData.year, baselineData.month)
                      }, (_, i) => i + 1).map(d => <option key={d} value={d.toString().padStart(2, '0')} data-id="element-2719">
                              {d}
                            </option>)}
                        </select>
                      </div>
                      <div data-id="element-2720">
                        <label htmlFor="year" className="block text-xs font-medium mb-2 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2721">
                          Year
                        </label>
                        <select id="year" value={baselineData.year} onChange={e => updateDateOfBirth(e.target.value, baselineData.month, baselineData.day)} className="w-full px-3 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2722">
                          <option value="" data-id="element-2723">Year</option>
                          {years.map(y => <option key={y} value={y} data-id="element-2724">
                              {y}
                            </option>)}
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* Time of Birth */}
                  <div data-id="element-2725">
                    <label className="block text-sm font-medium mb-3" data-id="element-2726">
                      Time of birth{' '}
                      <span className="text-[#F5F5F5]/40" data-id="element-2727">(optional)</span>
                    </label>
                    <div className="space-y-3 mb-4" data-id="element-2728">
                      {[{
                    value: 'exact',
                    label: 'I know the exact time'
                  }, {
                    value: 'approximate',
                    label: 'I know approximately'
                  }, {
                    value: 'not-sure',
                    label: "Not sure / Don't know"
                  }].map(option => <label key={option.value} className={`block border p-4 cursor-pointer transition-colors ${baselineData.timeKnown === option.value ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2729">
                          <input type="radio" name="timeKnown" value={option.value} checked={baselineData.timeKnown === option.value} onChange={e => setBaselineData(prev => ({
                      ...prev,
                      timeKnown: e.target.value,
                      timeOfBirth: '',
                      hour: '12',
                      minute: '00',
                      period: 'AM'
                    }))} className="sr-only" data-id="element-2730" />
                          <span className="text-sm" data-id="element-2731">{option.label}</span>
                        </label>)}
                    </div>
                    {(baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && <div className="mt-6" data-id="element-2732">
                        <div className="bg-[#111214] border border-[#2B2B2B] p-8" data-id="element-2733">
                          <div className="text-center mb-8" data-id="element-2734">
                            <div className="text-4xl font-medium tracking-tight" data-id="element-2735">
                              {baselineData.hour}:{baselineData.minute}{' '}
                              {baselineData.period}
                            </div>
                            <p className="text-xs text-[#F5F5F5]/40 mt-2" data-id="element-2736">
                              {baselineData.timeKnown === 'approximate' ? 'Your best estimate is fine' : 'Select the exact time'}
                            </p>
                          </div>
                          <div className="mb-6" data-id="element-2737">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2738">
                              Hour
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2739">
                              {['12', '01', '02', '03', '04', '05', '06', '07', '08', '09', '10', '11'].map(h => <button key={h} type="button" onClick={() => updateTimeOfBirth(h, baselineData.minute, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.hour === h ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2740">
                                  {h}
                                </button>)}
                            </div>
                          </div>
                          <div className="mb-6" data-id="element-2741">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2742">
                              Minute
                            </label>
                            <div className="grid grid-cols-6 gap-2" data-id="element-2743">
                              {['00', '15', '30', '45'].map(m => <button key={m} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, m, baselineData.period)} className={`py-3 text-sm font-medium transition-colors ${baselineData.minute === m ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2744">
                                  {m}
                                </button>)}
                            </div>
                            <div className="mt-2" data-id="element-2745">
                              <input type="number" min="0" max="59" value={baselineData.minute} onChange={e => {
                          const val = e.target.value.padStart(2, '0');
                          if (parseInt(val) >= 0 && parseInt(val) <= 59) updateTimeOfBirth(baselineData.hour, val, baselineData.period);
                        }} placeholder="Or type exact minute" className="w-full px-4 py-2 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] text-sm placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2746" />
                            </div>
                          </div>
                          <div data-id="element-2747">
                            <label className="block text-xs font-medium mb-3 text-[#F5F5F5]/60 uppercase tracking-wider" data-id="element-2748">
                              Period
                            </label>
                            <div className="grid grid-cols-2 gap-2" data-id="element-2749">
                              {['AM', 'PM'].map(p => <button key={p} type="button" onClick={() => updateTimeOfBirth(baselineData.hour, baselineData.minute, p)} className={`py-3 text-sm font-medium transition-colors ${baselineData.period === p ? 'bg-[#F5F5F5] text-[#0A0A0A]' : 'bg-[#0A0A0A] border border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2750">
                                  {p}
                                </button>)}
                            </div>
                          </div>
                        </div>
                      </div>}
                  </div>

                  <div data-id="element-2751">
                    <label htmlFor="placeOfBirth" className="block text-sm font-medium mb-2" data-id="element-2752">
                      Place of birth
                    </label>
                    <input type="text" id="placeOfBirth" value={baselineData.placeOfBirth} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  placeOfBirth: e.target.value
                }))} placeholder="City or town" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2753" />
                  </div>

                  <div data-id="element-2754">
                    <label htmlFor="country" className="block text-sm font-medium mb-2" data-id="element-2755">
                      Country
                    </label>
                    <input type="text" id="country" value={baselineData.country} onChange={e => setBaselineData(prev => ({
                  ...prev,
                  country: e.target.value
                }))} placeholder="Country" className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors" data-id="element-2756" />
                  </div>
                </div>

                <button onClick={handleBaselineSubmit} disabled={!baselineData.dateOfBirth || !baselineData.placeOfBirth || !baselineData.country || (baselineData.timeKnown === 'exact' || baselineData.timeKnown === 'approximate') && !baselineData.timeOfBirth} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2757">
                  Generate Structural Baseline
                </button>
              </div>}

            {/* PHASE 2: Work Baseline Output (Translation) */}
            {currentPhase === 'work-baseline-output' && <div data-id="element-2758">
                <div className="mb-12" data-id="element-2759">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2760">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2761">
                    Structural baseline — translated into work context
                  </p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2762">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2763">
                    <span className="text-2xl" data-id="element-2764">🧭</span>
                    <div className="flex-1" data-id="element-2765">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2766">
                        How you naturally operate at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2767">
                        {workBaselineOutput.workOrientation}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2768">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2769">
                    <span className="text-2xl" data-id="element-2770">🏗️</span>
                    <div className="flex-1" data-id="element-2771">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2772">
                        How this system responds to authority and pressure
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2773">
                        {workBaselineOutput.authorityResponse}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2774">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2775">
                    <span className="text-2xl" data-id="element-2776">⚠️</span>
                    <div className="flex-1" data-id="element-2777">
                      <h2 className="text-xl font-medium mb-3" data-id="element-2778">
                        Where this pattern creates friction at work
                      </h2>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2779">
                        {workBaselineOutput.workBlindSpot}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 text-center" data-id="element-2780">
                  <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider" data-id="element-2781">
                    Structural baseline — translated into work context
                  </p>
                </div>

                <div className="mb-12" data-id="element-2782">
                  <button onClick={() => setShowExplanation(!showExplanation)} className="flex items-center gap-2 text-sm text-[#F5F5F5]/70 hover:text-[#F5F5F5] transition-colors" data-id="element-2783">
                    <span data-id="element-2784">📘</span>
                    <span data-id="element-2785">How this work snapshot was generated</span>
                    <span className="text-xs" data-id="element-2786">
                      {showExplanation ? '▼' : '▶'}
                    </span>
                  </button>
                  {showExplanation && <div className="mt-4 p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2787">
                      <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2788">
                        This snapshot translates your structural baseline into
                        work systems — roles, authority, responsibility, and
                        output. The baseline does not change between life and
                        work. Only the contextual expression does. The next
                        questions tune how this pattern is expressing in your
                        current work environment.
                      </p>
                    </div>}
                </div>

                <div className="space-y-4" data-id="element-2789">
                  <button onClick={() => setCurrentPhase('work-calibration')} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2790">
                    🧩 See how this pattern is expressing at work right now
                  </button>
                  <div className="text-center" data-id="element-2791">
                    <p className="text-xs text-[#F5F5F5]/50 mb-2" data-id="element-2792">
                      Optional · ~30 seconds
                    </p>
                    <p className="text-xs text-[#F5F5F5]/40 leading-relaxed max-w-md mx-auto" data-id="element-2793">
                      This tunes expression, not structure.
                    </p>
                  </div>
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2794">
                    Skip for now
                  </button>
                </div>
              </div>}

            {/* PHASE 3: Work Calibration Questions */}
            {currentPhase === 'work-calibration' && <div data-id="element-2795">
                <div className="mb-12" data-id="element-2796">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2797">
                    Refine your work snapshot
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2798">~30 seconds</p>
                </div>

                <div className="space-y-12 mb-12" data-id="element-2799">
                  {/* Q1: Work Pressure Level */}
                  <div data-id="element-2800">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2801">
                      How intense does work pressure feel right now?
                    </h2>
                    <div className="space-y-3" data-id="element-2802">
                      {['Low', 'Moderate', 'High', 'Unsustainable'].map(level => <label key={level} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.pressureLevel === level ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2803">
                            <input type="radio" name="pressureLevel" value={level} checked={workCalibrationData.pressureLevel === level} onChange={e => setWorkCalibrationData(prev => ({
                      ...prev,
                      pressureLevel: e.target.value
                    }))} className="sr-only" data-id="element-2804" />
                            <span className="text-base" data-id="element-2805">{level}</span>
                          </label>)}
                    </div>
                  </div>

                  {/* Q2: Primary Work Friction (Multi-select) */}
                  <div data-id="element-2806">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2807">
                      Where do you feel the most friction at work lately?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-6" data-id="element-2808">
                      Select all that apply
                    </p>
                    <div className="space-y-3" data-id="element-2809">
                      {['Authority / management', 'Ambiguity of role or goals', 'Pace and deadlines', 'Ownership vs control', 'Collaboration / dependency'].map(friction => <label key={friction} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.frictionZones.includes(friction) ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2810">
                          <input type="checkbox" value={friction} checked={workCalibrationData.frictionZones.includes(friction)} onChange={() => handleToggleFriction(friction)} className="sr-only" data-id="element-2811" />
                          <span className="text-base" data-id="element-2812">{friction}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Q3: Mode Under Pressure */}
                  <div data-id="element-2813">
                    <h2 className="text-lg font-medium mb-6" data-id="element-2814">
                      When work pressure rises, you usually…
                    </h2>
                    <div className="space-y-3" data-id="element-2815">
                      {['Take more control', 'Pull back and observe', 'Push harder', 'Disengage emotionally'].map(mode => <label key={mode} className={`block border p-5 cursor-pointer transition-colors ${workCalibrationData.pressureMode === mode ? 'border-[#F5F5F5] bg-[#F5F5F5]/5' : 'border-[#2B2B2B] hover:border-[#F5F5F5]/30'}`} data-id="element-2816">
                          <input type="radio" name="pressureMode" value={mode} checked={workCalibrationData.pressureMode === mode} onChange={e => setWorkCalibrationData(prev => ({
                      ...prev,
                      pressureMode: e.target.value
                    }))} className="sr-only" data-id="element-2817" />
                          <span className="text-base" data-id="element-2818">{mode}</span>
                        </label>)}
                    </div>
                  </div>

                  {/* Optional Context */}
                  <div data-id="element-2819">
                    <h2 className="text-lg font-medium mb-3" data-id="element-2820">
                      Anything important about your current work situation?
                    </h2>
                    <p className="text-sm text-[#F5F5F5]/50 mb-4" data-id="element-2821">
                      Optional context
                    </p>
                    <textarea value={workCalibrationData.workContext} onChange={e => setWorkCalibrationData(prev => ({
                  ...prev,
                  workContext: e.target.value
                }))} rows={3} maxLength={200} className="w-full px-4 py-3 bg-[#0A0A0A] border border-[#2B2B2B] text-[#F5F5F5] placeholder-[#F5F5F5]/40 focus:border-[#F5F5F5]/50 focus:outline-none transition-colors resize-none" placeholder="Optional context..." data-id="element-2822" />
                    <p className="text-xs text-[#F5F5F5]/40 mt-2 text-right" data-id="element-2823">
                      {workCalibrationData.workContext.length}/200
                    </p>
                  </div>
                </div>

                <button onClick={handleWorkCalibrationSubmit} disabled={!workCalibrationData.pressureLevel || workCalibrationData.frictionZones.length === 0 || !workCalibrationData.pressureMode} className="w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg disabled:opacity-30 disabled:cursor-not-allowed" data-id="element-2824">
                  Apply Refinement
                </button>
              </div>}

            {/* PHASE 4: Integrated Work Output */}
            {currentPhase === 'integrated-work-output' && <div data-id="element-2825">
                <div className="mb-12" data-id="element-2826">
                  <h1 className="text-3xl md:text-4xl font-medium mb-3 leading-tight tracking-tight" data-id="element-2827">
                    Quick Work Decode
                  </h1>
                  <p className="text-sm text-[#F5F5F5]/50" data-id="element-2828">Refined Snapshot</p>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2829">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2830">
                    <span className="text-2xl" data-id="element-2831">🧱</span>
                    <div className="flex-1" data-id="element-2832">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2833">
                        Structural baseline at work
                      </p>
                      <p className="text-sm text-[#F5F5F5]/60 leading-relaxed" data-id="element-2834">
                        {integratedWorkOutput.structuralBaseline}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2835">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2836">
                    <span className="text-2xl" data-id="element-2837">🔄</span>
                    <div className="flex-1" data-id="element-2838">
                      <p className="text-xs text-[#F5F5F5]/40 uppercase tracking-wider mb-3" data-id="element-2839">
                        How this is expressing right now
                      </p>
                      <p className="text-[#F5F5F5]/70 leading-relaxed" data-id="element-2840">
                        {integratedWorkOutput.currentExpression}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2841">
                  <div className="flex items-start gap-4 mb-4" data-id="element-2842">
                    <span className="text-2xl" data-id="element-2843">🎯</span>
                    <div className="flex-1" data-id="element-2844">
                      <p className="text-lg font-medium text-[#F5F5F5]/90 italic" data-id="element-2845">
                        "{integratedWorkOutput.workTensionSnapshot}"
                      </p>
                    </div>
                  </div>
                </div>

                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2846">
                  <button onClick={() => setShowWhatChanged(!showWhatChanged)} className="flex items-center gap-2 text-base font-medium text-[#F5F5F5] hover:text-[#F5F5F5]/80 transition-colors mb-6" data-id="element-2847">
                    <span data-id="element-2848">🔍</span>
                    <span data-id="element-2849">What changed after refinement</span>
                    <span className="text-xs" data-id="element-2850">
                      {showWhatChanged ? '▼' : '▶'}
                    </span>
                  </button>
                  {showWhatChanged && <div className="space-y-6" data-id="element-2851">
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2852">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2853">
                          What baseline showed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2854">
                          {integratedWorkOutput.whatChanged.baseline}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2855">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2856">
                          What work calibration revealed
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2857">
                          {integratedWorkOutput.whatChanged.workContext}
                        </p>
                      </div>
                      <div className="p-6 bg-[#111214] border border-[#2B2B2B]" data-id="element-2858">
                        <h3 className="text-sm font-medium mb-4" data-id="element-2859">
                          Why baseline alone could not show this
                        </h3>
                        <p className="text-sm text-[#F5F5F5]/70 leading-relaxed" data-id="element-2860">
                          {integratedWorkOutput.whatChanged.whyBaselineAlone}
                        </p>
                      </div>
                    </div>}
                </div>

                {/* Deep Work Decode Bridge */}
                <div className="mb-12 pb-12 border-b border-[#2B2B2B]" data-id="element-2861">
                  <h2 className="text-xl font-medium mb-6" data-id="element-2862">
                    What this snapshot does not cover
                  </h2>
                  <div className="space-y-4 text-[#F5F5F5]/70 leading-relaxed" data-id="element-2863">
                    <p data-id="element-2864">
                      This snapshot shows{' '}
                      <strong className="text-[#F5F5F5]" data-id="element-2865">
                        how your structure interacts with work right now
                      </strong>
                      .
                    </p>
                    <p data-id="element-2866">It does not map:</p>
                    <ul className="space-y-2 ml-4" data-id="element-2867">
                      <li data-id="element-2868">• how this pattern developed across your career</li>
                      <li data-id="element-2869">• how it affects leadership, growth, or burnout</li>
                      <li data-id="element-2870">
                        • where work amplifies or suppresses your strengths
                      </li>
                      <li data-id="element-2871">
                        • how role, environment, and authority interact
                        long-term
                      </li>
                    </ul>
                  </div>
                </div>

                <div className="mb-12" data-id="element-2872">
                  <Link to="/report-detail?report=deep-work-decode" className="block w-full px-8 py-5 bg-[#F5F5F5] text-[#0A0A0A] text-center font-semibold text-sm tracking-widest uppercase hover:bg-[#F5F5F5]/90 transition-colors shadow-lg" data-id="element-2873">
                    Explore Deep Work Decode
                  </Link>
                  <p className="text-xs text-[#F5F5F5]/40 text-center mt-3" data-id="element-2874">
                    A full diagnostic of your work-pattern architecture
                  </p>
                </div>

                {/* Exit Options */}
                <div className="space-y-4 pt-8 border-t border-[#2B2B2B]" data-id="element-2875">
                  <button onClick={() => navigate('/decode')} className="w-full px-8 py-4 border border-[#F5F5F5]/30 text-[#F5F5F5]/70 font-medium text-sm tracking-wide hover:bg-[#F5F5F5]/5 transition-colors" data-id="element-2876">
                    💾 Save Snapshot
                  </button>
                  <button onClick={() => {
                setCurrentPhase('work-baseline-output');
                setWorkCalibrationData({
                  pressureLevel: '',
                  frictionZones: [],
                  pressureMode: '',
                  workContext: ''
                });
              }} className="w-full px-8 py-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors" data-id="element-2877">
                    🔁 Run another Quick Decode
                  </button>
                  <Link to="/report-detail?report=deep-self-decode" className="block w-full px-8 py-4 text-sm text-[#F5F5F5]/50 hover:text-[#F5F5F5]/70 transition-colors text-center" data-id="element-2878">
                    ➡️ Explore Deep Self Decode
                  </Link>
                </div>
              </div>}
          </div>
        </main>
      </div>
    </>;
}